import { Grid, Typography, InputAdornment, MenuItem } from '@mui/material'
import { ControlledSelect, ControlledTextField } from '@if/mui'
import { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { EPaymentPlan } from '@if/core/types'
import { PersonalLoanCalculatorSchemaType } from '@src/types/PersonalLoanCalculatorSchema'
import { PersonalLoanCalculatorGridSizes } from '@src/types/Constants'
import { PaymentOption, TermOption } from '../personalLoanCalculator-hooks'

interface LoanDetailsFormProps {
  control: Control<PersonalLoanCalculatorSchemaType>
  merchantPaymentData?: PaymentOption[]
  termData: TermOption[]
  stableTermData: TermOption[]
  disabled?: boolean
}

export const LoanDetailsForm = ({
  control,
  merchantPaymentData,
  termData,
  stableTermData,
  disabled = false,
}: LoanDetailsFormProps) => {
  const { t } = useTranslation()

  const paymentOptions = useMemo(() => {
    return (
      merchantPaymentData?.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label.includes(EPaymentPlan.RegularDailyInterests)
            ? t(`EPaymentPlan.${EPaymentPlan.RegularDailyInterests}`, { ns: 'enum' })
            : option.label}
        </MenuItem>
      )) || []
    )
  }, [merchantPaymentData, t])

  const termOptions = useMemo(() => {
    const activeTermData = stableTermData && stableTermData.length > 0 ? stableTermData : termData || []
    return activeTermData.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ))
  }, [stableTermData, termData])

  return (
    <Grid container spacing={2}>
      <Grid size={PersonalLoanCalculatorGridSizes.FORM_FIELD}>
        <Typography variant="body2" gutterBottom>
          {t('worksheet.requestedAmount')}
        </Typography>
        <ControlledTextField
          label=""
          control={control}
          name="amountRequested"
          fullWidth
          disabled={disabled}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Typography component="span" sx={{ color: 'text.secondary' }}>
                    $
                  </Typography>
                </InputAdornment>
              ),
            },
          }}
          sx={{
            '& input': {
              textAlign: 'left',
            },
          }}
        />
      </Grid>

      <Grid size={PersonalLoanCalculatorGridSizes.FORM_FIELD}>
        <Typography variant="body2" gutterBottom>
          {t('worksheet.paymentPlan')}
        </Typography>
        <ControlledSelect
          control={control}
          name="paymentPlanId"
          label=""
          disableEmptyOption
          fullWidth
          options={paymentOptions}
          disabled={disabled || !merchantPaymentData || merchantPaymentData.length === 0}
        />
      </Grid>

      <Grid size={PersonalLoanCalculatorGridSizes.FORM_FIELD}>
        <Typography variant="body2" gutterBottom>
          {t('worksheet.loanTerm')}
        </Typography>
        <ControlledSelect
          control={control}
          name="term"
          label=""
          disableEmptyOption
          fullWidth
          options={termOptions}
          disabled={disabled || termOptions.length === 0}
        />
      </Grid>
    </Grid>
  )
}
